import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { getScoreStatus } from "@/lib/assessment/scoring";
import type { AssessmentScores } from "@/lib/assessment/scoring";

interface PathwayComparisonProps {
  pathways: {
    id: string;
    title: string;
    match: number;
    scores: AssessmentScores;
    requirements: {
      met: string[];
      missing: string[];
    };
  }[];
}

const PathwayComparison = ({ pathways }: PathwayComparisonProps) => {
  const getStatusBadge = (score: number) => {
    const status = getScoreStatus(score);
    const colors = {
      success: "bg-green-500/10 text-green-500",
      warning: "bg-yellow-500/10 text-yellow-500",
      error: "bg-red-500/10 text-red-500"
    };

    return (
      <Badge variant="secondary" className={colors[status]}>
        {score}%
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Requirements Analysis</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {pathways.map((pathway) => (
          <div key={pathway.id} className="space-y-6">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">{pathway.title}</h3>
                {getStatusBadge(pathway.match)}
              </div>
              <Progress value={pathway.match} className="h-2" />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-medium">Requirements Met</h4>
                <ul className="space-y-2">
                  {pathway.requirements.met.map((req, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Areas for Improvement</h4>
                <ul className="space-y-2">
                  {pathway.requirements.missing.map((req, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mt-1.5" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default PathwayComparison;